import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  FileJson,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Trash2,
  Eye,
  Download as DownloadIcon,
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDropzone } from 'react-dropzone';

export default function DataExportImportPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('export');
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [exportType, setExportType] = useState('items');
  const [importType, setImportType] = useState('items');
  const [exportFormat, setExportFormat] = useState('csv');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Fetch export jobs
  const { data: exportJobs = [] } = useQuery({
    queryKey: ['export-jobs'],
    queryFn: () => api.getExportJobs(),
  });

  // Fetch import jobs
  const { data: importJobs = [] } = useQuery({
    queryKey: ['import-jobs'],
    queryFn: () => api.getImportJobs(),
  });

  // Fetch export types
  const { data: exportTypes } = useQuery({
    queryKey: ['export-types'],
    queryFn: () => api.getExportTypes(),
  });

  // Fetch import types
  const { data: importTypes } = useQuery({
    queryKey: ['import-types'],
    queryFn: () => api.getImportTypes(),
  });

  // Fetch import templates
  const { data: templates = [] } = useQuery({
    queryKey: ['import-templates'],
    queryFn: () => api.getImportTemplates(),
  });

  // Create export job mutation
  const createExportMutation = useMutation({
    mutationFn: (data: any) => api.createExportJob(data),
    onSuccess: () => {
      toast({ title: 'Export job created' });
      setShowExportDialog(false);
      queryClient.invalidateQueries({ queryKey: ['export-jobs'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Create import job mutation
  const createImportMutation = useMutation({
    mutationFn: (data: any) => api.createImportJob(data),
    onSuccess: (data) => {
      setImportJobId(data.job_id);
      toast({ title: 'Import job created. Upload your file.' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Upload file mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ jobId, file }: { jobId: string; file: File }) => {
      // Simulate progress
      setUploadProgress(0);
      const interval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 20, 90));
      }, 200);
      
      try {
        const result = await api.uploadImportFile(jobId, file);
        clearInterval(interval);
        setUploadProgress(100);
        return result;
      } catch (error) {
        clearInterval(interval);
        throw error;
      }
    },
    onSuccess: () => {
      toast({ title: 'File uploaded and validated' });
      queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Execute import mutation
  const executeImportMutation = useMutation({
    mutationFn: (jobId: string) => api.executeImport(jobId),
    onSuccess: () => {
      toast({ title: 'Import completed' });
      setShowImportDialog(false);
      setUploadedFile(null);
      setImportJobId(null);
      queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete job mutations
  const deleteExportMutation = useMutation({
    mutationFn: (id: string) => api.deleteExportJob(id),
    onSuccess: () => {
      toast({ title: 'Export job deleted' });
      queryClient.invalidateQueries({ queryKey: ['export-jobs'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const deleteImportMutation = useMutation({
    mutationFn: (id: string) => api.deleteImportJob(id),
    onSuccess: () => {
      toast({ title: 'Import job deleted' });
      queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Download template mutation
  const downloadTemplateMutation = useMutation({
    mutationFn: (templateId: string) => api.downloadImportTemplate(templateId),
    onSuccess: (data) => {
      // Create and download CSV file
      const blob = new Blob([data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  });

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
    maxFiles: 1,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'processing':
      case 'importing':
      case 'validating': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return CheckCircle2;
      case 'failed': return XCircle;
      case 'processing':
      case 'importing':
      case 'validating': return Loader2;
      default: return AlertCircle;
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="h-8 w-8" />
            Data Export & Import
          </h1>
          <p className="text-muted-foreground">
            Export data to CSV/Excel or import from files
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="export">Export Data</TabsTrigger>
          <TabsTrigger value="import">Import Data</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="export">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Export Card */}
            <Card>
              <CardHeader>
                <CardTitle>Export Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-6 border-2 border-dashed rounded-lg hover:border-primary/50 transition-colors">
                  <div className="p-4 bg-green-100 rounded-full">
                    <Download className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Export to File</h3>
                    <p className="text-sm text-muted-foreground">
                      Download your data in CSV, Excel, or JSON format
                    </p>
                  </div>
                  <Button onClick={() => setShowExportDialog(true)}>
                    Create Export
                  </Button>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Available Export Types</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {exportTypes?.types?.map((type: any) => (
                      <div key={type.key} className="flex items-center gap-2 p-2 border rounded">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{type.key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Exports */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Exports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {exportJobs?.slice(0, 5).map((job: any) => {
                    const StatusIcon = getStatusIcon(job.status);
                    return (
                      <div key={job.job_id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <StatusIcon className={`h-5 w-5 ${job.status === 'completed' ? 'text-green-500' : 'text-gray-400'}`} />
                          <div>
                            <p className="font-medium">{job.job_type}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(job.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                      </div>
                    );
                  })}
                  {exportJobs?.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No recent exports</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="import">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Import Card */}
            <Card>
              <CardHeader>
                <CardTitle>Import Data</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div
                  {...getRootProps()}
                  className={`flex items-center gap-4 p-6 border-2 border-dashed rounded-lg transition-colors cursor-pointer ${
                    isDragActive ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                >
                  <input {...getInputProps()} />
                  <div className="p-4 bg-blue-100 rounded-full">
                    <Upload className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">Import from File</h3>
                    <p className="text-sm text-muted-foreground">
                      Drag and drop or click to select CSV/Excel file
                    </p>
                  </div>
                  <Button onClick={() => setShowImportDialog(true)}>
                    Start Import
                  </Button>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Supported Import Types</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['items', 'vendors', 'locations']?.map((type) => (
                      <div key={type} className="flex items-center gap-2 p-2 border rounded">
                        <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm capitalize">{type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Imports */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Imports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {importJobs?.slice(0, 5).map((job: any) => {
                    const StatusIcon = getStatusIcon(job.status);
                    return (
                      <div key={job.job_id} className="p-3 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <StatusIcon className={`h-5 w-5 ${job.status === 'completed' ? 'text-green-500' : 'text-gray-400'}`} />
                            <div>
                              <p className="font-medium">{job.job_type}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(job.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                        </div>
                        {job.status === 'completed' && (
                          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                            <span>Created: {job.created_rows}</span>
                            <span>Updated: {job.updated_rows}</span>
                            {job.error_rows > 0 && (
                              <span className="text-red-500">Errors: {job.error_rows}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {importJobs?.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No recent imports</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Export & Import History</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="exports">
                <TabsList>
                  <TabsTrigger value="exports">Exports</TabsTrigger>
                  <TabsTrigger value="imports">Imports</TabsTrigger>
                </TabsList>
                <TabsContent value="exports">
                  <div className="space-y-3">
                    {exportJobs?.map((job: any) => (
                      <div key={job.job_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <FileText className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{job.job_type} - {job.export_format}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(job.created_at).toLocaleString()} • {job.total_rows || 0} rows
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                          {job.status === 'completed' && (
                            <Button variant="ghost" size="sm">
                              <DownloadIcon className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteExportMutation.mutate(job.job_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="imports">
                  <div className="space-y-3">
                    {importJobs?.map((job: any) => (
                      <div key={job.job_id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{job.job_type}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(job.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(job.status)}>{job.status}</Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteImportMutation.mutate(job.job_id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        {job.status === 'completed' && (
                          <div className="mt-3 flex gap-4 text-xs">
                            <span className="text-green-600">Created: {job.created_rows}</span>
                            <span className="text-blue-600">Updated: {job.updated_rows}</span>
                            {job.error_rows > 0 && (
                              <span className="text-red-500">Errors: {job.error_rows}</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Import Templates</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates?.map((template: any) => (
                  <Card key={template.template_id}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{template.template_name}</h3>
                          <Badge variant="outline" className="mt-1">{template.template_type}</Badge>
                        </div>
                        <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex items-center gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => downloadTemplateMutation.mutate(template.template_id)}
                        >
                          <Download className="mr-1 h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {templates?.length === 0 && (
                  <div className="col-span-full text-center py-8 text-muted-foreground">
                    No templates available
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Export Type</label>
              <Select value={exportType} onValueChange={setExportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {exportTypes?.types?.map((type: any) => (
                    <SelectItem key={type.key} value={type.key}>{type.key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Format</label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" /> CSV
                    </div>
                  </SelectItem>
                  <SelectItem value="xlsx">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4" /> Excel
                    </div>
                  </SelectItem>
                  <SelectItem value="json">
                    <div className="flex items-center gap-2">
                      <FileJson className="h-4 w-4" /> JSON
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>
              Cancel
            </Button>
            <Button onClick={() => createExportMutation.mutate({
              export_type: exportType,
              export_format: exportFormat,
            })}>
              Create Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Import Type</label>
              <Select value={importType} onValueChange={setImportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['items', 'vendors', 'locations']?.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div
              {...getRootProps()}
              className={`p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                isDragActive ? 'border-primary bg-primary/5' : ''
              }`}
            >
              <input {...getInputProps()} />
              {uploadedFile ? (
                <div>
                  <FileSpreadsheet className="h-8 w-8 mx-auto mb-2 text-primary" />
                  <p className="font-medium">{uploadedFile.name}</p>
                  <Button variant="ghost" size="sm" onClick={() => setUploadedFile(null)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <div>
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drop file here or click to select
                  </p>
                </div>
              )}
            </div>
            {uploadProgress > 0 && (
              <Progress value={uploadProgress} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowImportDialog(false);
              setUploadedFile(null);
              setImportJobId(null);
            }}>
              Cancel
            </Button>
            {!importJobId ? (
              <Button
                onClick={() => createImportMutation.mutate({
                  job_type: importType,
                  import_mode: 'create_update',
                })}
              >
                Start Import
              </Button>
            ) : (
              <Button
                onClick={() => uploadedFile && uploadMutation.mutate({ jobId: importJobId, file: uploadedFile })}
                disabled={!uploadedFile || uploadMutation.isPending}
              >
                {uploadMutation.isPending ? 'Uploading...' : 'Upload & Validate'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
